import axios from 'axios';
import type {
  HealthResponse,
  SeedListResponse,
  SeedResponse,
  CreateSeedRequest,
  UpdateSeedRequest,
  BatchListResponse,
  BatchResponse,
  BatchDetailResponse,
  CreateBatchRequest,
  BatchResultsResponse,
  UpdateBatchRequest,
  FilterState,
  ScopeState,
  TagWithUsage,
} from './types';

const API_BASE = '/api/v1/self-play';

export async function checkHealth(): Promise<HealthResponse> {
  const response = await axios.get<HealthResponse>('/health');
  return response.data;
}

export async function checkReady(): Promise<HealthResponse> {
  const response = await axios.get<HealthResponse>('/ready');
  return response.data;
}

// Seeds API
export async function listSeeds(
  limit?: number,
  offset?: number,
  filter?: FilterState,
  scope?: ScopeState
): Promise<SeedListResponse> {
  const params = new URLSearchParams();
  if (limit !== undefined) params.set('limit', String(limit));
  if (offset !== undefined) params.set('offset', String(offset));
  if (filter?.text_search.trim()) params.set('text_search', filter.text_search.trim());
  if (filter?.untagged) params.set('untagged', filter.untagged);
  for (const value of filter?.include_tags ?? []) params.append('include_tags', value);
  for (const value of filter?.exclude_tags ?? []) params.append('exclude_tags', value);
  if (scope?.project_id) params.set('project_id', scope.project_id);
  const query = params.toString();
  const url = `${API_BASE}/seeds${query ? `?${query}` : ''}`;
  const response = await axios.get<SeedListResponse>(url);
  return response.data;
}

const SEEDS_FETCH_PAGE = 100;

/** Loads every seed via paginated list calls (for export). */
export async function fetchAllSeeds(
  filter?: FilterState,
  scope?: ScopeState
): Promise<SeedResponse[]> {
  const all: SeedResponse[] = [];
  let offset = 0;
  while (true) {
    const response = await listSeeds(SEEDS_FETCH_PAGE, offset, filter, scope);
    all.push(...response.seeds);
    if (
      response.seeds.length < SEEDS_FETCH_PAGE ||
      all.length >= response.total
    ) {
      break;
    }
    offset += SEEDS_FETCH_PAGE;
  }
  return all;
}

export async function getSeed(seedId: string): Promise<SeedResponse> {
  const response = await axios.get<SeedResponse>(`${API_BASE}/seeds/${seedId}`);
  return response.data;
}

export async function createSeed(data: CreateSeedRequest): Promise<SeedResponse> {
  const response = await axios.post<SeedResponse>(`${API_BASE}/seeds`, data);
  return response.data;
}

export async function updateSeed(
  seedId: string,
  data: UpdateSeedRequest
): Promise<SeedResponse> {
  const response = await axios.put<SeedResponse>(
    `${API_BASE}/seeds/${seedId}`,
    data
  );
  return response.data;
}

export async function deleteSeed(seedId: string): Promise<void> {
  await axios.delete(`${API_BASE}/seeds/${seedId}`);
}


// Batches API
export async function listBatches(
  limit?: number,
  offset?: number,
  status?: string,
  filter?: FilterState,
  scope?: ScopeState
): Promise<BatchListResponse> {
  const params = new URLSearchParams();
  if (limit !== undefined) params.set('limit', String(limit));
  if (offset !== undefined) params.set('offset', String(offset));
  if (status !== undefined) params.set('status', status);
  if (filter?.text_search.trim()) params.set('text_search', filter.text_search.trim());
  if (filter?.untagged) params.set('untagged', filter.untagged);
  for (const value of filter?.include_tags ?? []) params.append('include_tags', value);
  for (const value of filter?.exclude_tags ?? []) params.append('exclude_tags', value);
  if (scope?.project_id) params.set('project_id', scope.project_id);
  const query = params.toString();
  const url = `${API_BASE}/batches${query ? `?${query}` : ''}`;
  const response = await axios.get<BatchListResponse>(url);
  return response.data;
}

export async function getBatch(batchId: string): Promise<BatchDetailResponse> {
  const response = await axios.get<BatchDetailResponse>(
    `${API_BASE}/batches/${batchId}`
  );
  return response.data;
}

export async function createBatch(
  data: CreateBatchRequest
): Promise<BatchResponse> {
  const response = await axios.post<BatchResponse>(`${API_BASE}/batches`, data);
  return response.data;
}

export async function updateBatch(
  batchId: string,
  data: UpdateBatchRequest
): Promise<BatchResponse> {
  const response = await axios.put<BatchResponse>(`${API_BASE}/batches/${batchId}`, data);
  return response.data;
}

export async function runBatch(batchId: string): Promise<BatchResponse> {
  const response = await axios.post<BatchResponse>(
    `${API_BASE}/batches/${batchId}/run`
  );
  return response.data;
}

export async function getBatchResults(
  batchId: string
): Promise<BatchResultsResponse> {
  const response = await axios.get<BatchResultsResponse>(
    `${API_BASE}/batches/${batchId}/results`
  );
  return response.data;
}

export async function exportBatchCsv(batchId: string): Promise<Blob> {
  const response = await axios.get(`${API_BASE}/batches/${batchId}/results/csv`, {
    responseType: 'blob',
  });
  return response.data;
}

export async function deleteBatch(batchId: string): Promise<void> {
  await axios.delete(`${API_BASE}/batches/${batchId}`);
}

export interface TagsListResponse {
  tags: string[];
}

export interface TagsWithUsageListResponse {
  tags: TagWithUsage[];
}

export async function listTags(scope?: ScopeState): Promise<TagsListResponse> {
  const params = new URLSearchParams();
  if (scope?.project_id) params.set('project_id', scope.project_id);
  const query = params.toString();
  const response = await axios.get<TagsListResponse>(
    `${API_BASE}/tags${query ? `?${query}` : ''}`
  );
  return response.data;
}

export async function listTagsWithUsage(
  scope?: ScopeState
): Promise<TagsWithUsageListResponse> {
  const params = new URLSearchParams();
  if (scope?.project_id) params.set('project_id', scope.project_id);
  const query = params.toString();
  const response = await axios.get<TagsWithUsageListResponse>(
    `${API_BASE}/tags/with-usage${query ? `?${query}` : ''}`
  );
  return response.data;
}

export async function createTag(
  name: string,
  scope?: ScopeState
): Promise<{ name: string }> {
  const payload: Record<string, unknown> = { name };
  if (scope?.project_id) payload.project_id = scope.project_id;
  const response = await axios.post<{ name: string }>(`${API_BASE}/tags`, payload);
  return response.data;
}

export async function listProjects(): Promise<string[]> {
  const response = await axios.get<{ project_ids: string[] }>(
    `${API_BASE}/projects`
  );
  return response.data.project_ids;
}

export async function deleteTag(name: string, scope?: ScopeState): Promise<void> {
  const params = new URLSearchParams();
  if (scope?.project_id) params.set('project_id', scope.project_id);
  const query = params.toString();
  await axios.delete(
    `${API_BASE}/tags/${encodeURIComponent(name)}${query ? `?${query}` : ''}`
  );
}

